import { useMemo, useState } from 'react';

import {
  ArrowRightLeft,
  BarChart3,
  Bell,
  Bot,
  Brain,
  CalendarClock,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Code,
  CornerDownLeft,
  CreditCard,
  Search as SearchIcon,
  Database,
  GitBranch,
  Globe,
  Link2,
  ListFilter,
  Mail,
  Megaphone,
  MessageSquare,
  Package,
  PhoneOutgoing,
  Search,
  ShieldAlert,
  ShieldCheck,
  Square,
  Tag,
  UserPen,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useFeaturesStore } from '@/stores/features.store';

import { NODE_COLORS } from './workflow-utils';

interface PaletteItem {
  type: string;
  label: string;
  icon: React.ReactNode;
  colors: (typeof NODE_COLORS)[string];
}

interface PaletteCategory {
  name: string;
  items: PaletteItem[];
}

const PALETTE_CATEGORIES: PaletteCategory[] = [
  {
    name: 'Logic',
    items: [
      {
        type: 'condition',
        label: 'Condition',
        icon: <GitBranch size={13} />,
        colors: NODE_COLORS.condition,
      },
      {
        type: 'end',
        label: 'End',
        icon: <Square size={13} />,
        colors: NODE_COLORS.end,
      },
      {
        type: 'return',
        label: 'Return',
        icon: <CornerDownLeft size={13} />,
        colors: NODE_COLORS.return,
      },
      {
        type: 'function',
        label: 'Function',
        icon: <Code size={13} />,
        colors: NODE_COLORS.function,
      },
    ],
  },
  {
    name: 'Communication',
    items: [
      {
        type: 'send_email',
        label: 'Email',
        icon: <Mail size={13} />,
        colors: NODE_COLORS.send_email,
      },
      {
        type: 'send_sms',
        label: 'SMS',
        icon: <MessageSquare size={13} />,
        colors: NODE_COLORS.send_sms,
      },
      {
        type: 'outbound_call',
        label: 'Outbound Call',
        icon: <PhoneOutgoing size={13} />,
        colors: NODE_COLORS.outbound_call,
      },
      {
        type: 'notification',
        label: 'Notification',
        icon: <Bell size={13} />,
        colors: NODE_COLORS.notification,
      },
      {
        type: 'campaign',
        label: 'Create Campaign',
        icon: <Megaphone size={13} />,
        colors: NODE_COLORS.campaign,
      },
    ],
  },
  {
    name: 'Data',
    items: [
      {
        type: 'update_call',
        label: 'Update Call',
        icon: <Database size={13} />,
        colors: NODE_COLORS.update_call,
      },
      {
        type: 'update_contact',
        label: 'Contact',
        icon: <UserPen size={13} />,
        colors: NODE_COLORS.update_contact,
      },
      {
        type: 'enrich_data',
        label: 'Enrich Data',
        icon: <Search size={13} />,
        colors: NODE_COLORS.enrich_data,
      },
      {
        type: 'stock',
        label: 'Stock Lookup',
        icon: <Package size={13} />,
        colors: NODE_COLORS.stock,
      },
      {
        type: 'collect',
        label: 'Collect',
        icon: <ListFilter size={13} />,
        colors: NODE_COLORS.collect,
      },
    ],
  },
  {
    name: 'AI',
    items: [
      {
        type: 'agent',
        label: 'Agent',
        icon: <Bot size={13} />,
        colors: NODE_COLORS.agent,
      },
      {
        type: 'run_review',
        label: 'AI Review',
        icon: <Brain size={13} />,
        colors: NODE_COLORS.run_review,
      },
      {
        type: 'run_classifier',
        label: 'Classifier',
        icon: <Tag size={13} />,
        colors: NODE_COLORS.run_classifier,
      },
      {
        type: 'run_quality',
        label: 'Quality',
        icon: <ShieldCheck size={13} />,
        colors: NODE_COLORS.run_quality,
      },
      {
        type: 'handoff',
        label: 'AI Handoff',
        icon: <ArrowRightLeft size={13} />,
        colors: NODE_COLORS.handoff,
      },
      {
        type: 'schedule_workflow',
        label: 'Schedule Workflow',
        icon: <CalendarClock size={13} />,
        colors: NODE_COLORS.schedule_workflow,
      },
    ],
  },
  {
    name: 'Analytics',
    items: [
      {
        type: 'get_analytics',
        label: 'Get Analytics',
        icon: <BarChart3 size={13} />,
        colors: NODE_COLORS.get_analytics,
      },
      {
        type: 'get_quality_issues',
        label: 'Quality Issues',
        icon: <ShieldAlert size={13} />,
        colors: NODE_COLORS.get_quality_issues,
      },
      {
        type: 'get_review_summary',
        label: 'Review Summary',
        icon: <ClipboardList size={13} />,
        colors: NODE_COLORS.get_review_summary,
      },
    ],
  },
  {
    name: 'Integrations',
    items: [
      {
        type: 'call_api',
        label: 'Call API',
        icon: <Globe size={13} />,
        colors: NODE_COLORS.call_api,
      },
      {
        type: 'create_payment',
        label: 'Create Payment',
        icon: <CreditCard size={13} />,
        colors: NODE_COLORS.create_payment,
      },
      {
        type: 'tracking_link',
        label: 'Tracking Link',
        icon: <Link2 size={13} />,
        colors: NODE_COLORS.tracking_link,
      },
      {
        type: 'booking',
        label: 'Booking',
        icon: <CalendarDays size={13} />,
        colors: NODE_COLORS.booking,
      },
    ],
  },
];

interface NodePaletteProps {
  open: boolean;
  onToggle: () => void;
}

export const NodePalette = ({ open, onToggle }: NodePaletteProps) => {
  const { bookings: featureBookings } = useFeaturesStore();
  const [query, setQuery] = useState('');

  const handleDragStart = (event: React.DragEvent, type: string) => {
    event.dataTransfer.setData('application/workflow-node-type', type);
    event.dataTransfer.effectAllowed = 'move';
  };

  const categories = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PALETTE_CATEGORIES.map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) =>
          (item.type !== 'booking' || featureBookings) &&
          (q === '' ||
            item.label.toLowerCase().includes(q) ||
            item.type.replace(/_/g, ' ').includes(q)),
      ),
    })).filter((cat) => cat.items.length > 0);
  }, [query, featureBookings]);

  const isEmpty = categories.length === 0;

  return (
    <aside
      className={`bg-background flex flex-col border-r transition-all duration-200 ${open ? 'w-56' : 'w-9'}`}
    >
      {open ? (
        <div className="flex h-full w-56 flex-col">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
              Nodes
            </span>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onToggle}>
              <ChevronLeft size={14} />
            </Button>
          </div>

          <div className="border-b p-2">
            <div className="relative">
              <SearchIcon
                size={13}
                className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 -translate-y-1/2"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search nodes…"
                aria-label="Search nodes"
                className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring h-7 w-full rounded-md border pr-2 pl-7 text-xs focus-visible:ring-1 focus-visible:outline-none"
              />
            </div>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto p-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {isEmpty && (
              <p className="text-muted-foreground px-1 py-6 text-center text-[11px]">
                No nodes match &ldquo;{query}&rdquo;
              </p>
            )}
            {categories.map((cat) => (
              <div key={cat.name}>
                <p className="text-muted-foreground mb-1.5 px-1 text-[10px] font-semibold tracking-wide uppercase">
                  {cat.name}
                </p>
                <div className="space-y-0.5">
                  {cat.items.map((item) => (
                    <div
                      key={item.type}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item.type)}
                      title={`Drag ${item.label} onto the canvas`}
                      // Neutral row with a tinted icon square: the same visual
                      // language as the node it drops as, so the palette reads
                      // as a list of nodes rather than a pile of colour.
                      className="hover:bg-muted/70 group flex cursor-grab items-center gap-2 rounded-md px-1.5 py-1.5 transition-colors active:cursor-grabbing"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${item.colors.tint} ${item.colors.icon}`}
                      >
                        {item.icon}
                      </span>
                      <span className="text-foreground truncate text-[11px] font-medium">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex h-full w-9 flex-col items-center pt-2">
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={onToggle}
            title="Show node palette"
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      )}
    </aside>
  );
};
